import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, Filter, X, ArrowRight, Clock, BookOpen, Calendar, Users, MapPin, Zap } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { SEO, getCourseListSchema } from "../components/SEO";
import { PromoPriceDisplay, PromoInlinePrice } from "../components/PromoPriceDisplay";
import { SelfPacedBadge } from "../components/SelfPacedBadge";
import { upcomingCohorts, cohortDayRange, cohortMonth, cohortYear } from "../lib/cohorts";
import { PROMO_ACTIVE, PROMO_BADGE_TEXT } from "../utils/promoUtils";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Courses = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [courses, setCourses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [levels, setLevels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedFormats, setSelectedFormats] = useState({});
  const [courseSchedules, setCourseSchedules] = useState({});
  const [loadingSchedules, setLoadingSchedules] = useState({});

  const [filters, setFilters] = useState({
    search: searchParams.get("search") || "",
    category: searchParams.get("category") || "",
    certification_level: searchParams.get("level") || "",
    delivery_format: searchParams.get("format") || "",
    page: parseInt(searchParams.get("page")) || 1,
  });

  const deliveryFormats = [
    { value: "self-study", label: "Self-Study" },
    { value: "online-live", label: "Online Live" },
    { value: "e-learning", label: "E-Learning" },
  ];

  // Fetch schedules when Online Live is selected
  const fetchSchedulesForCourse = async (courseId) => {
    if (courseSchedules[courseId] || loadingSchedules[courseId]) return;
    
    setLoadingSchedules(prev => ({ ...prev, [courseId]: true }));
    try {
      const response = await axios.get(`${API}/schedules/course/${courseId}`);
      setCourseSchedules(prev => ({ ...prev, [courseId]: response.data.schedules }));
    } catch (error) {
      console.error("Error fetching schedules:", error);
      setCourseSchedules(prev => ({ ...prev, [courseId]: [] }));
    } finally {
      setLoadingSchedules(prev => ({ ...prev, [courseId]: false }));
    }
  };

  // Courses with planned live dates default to Online Live so the dates show first
  const fmtOf = (course) =>
    selectedFormats[course.id] ||
    (upcomingCohorts(course, "online-live").length && course.delivery_formats.some((f) => f.format === "online-live")
      ? "online-live"
      : course.delivery_formats[0]?.format);

  // Handle format change
  const handleFormatChange = (courseId, format) => {
    setSelectedFormats(prev => ({ ...prev, [courseId]: format }));
    if (format === "online-live") {
      fetchSchedulesForCourse(courseId);
    }
  };

  const formatScheduleDate = (dateStr) => {
    return new Date(dateStr + "T00:00:00").toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric"
    });
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Seed database first
        await axios.post(`${API}/seed`).catch(() => {});
        
        const [coursesRes, categoriesRes, levelsRes, countRes] = await Promise.all([
          axios.get(`${API}/courses`, { params: filters }),
          axios.get(`${API}/courses/categories`),
          axios.get(`${API}/courses/levels`),
          axios.get(`${API}/courses/count`, { params: filters }),
        ]);
        
        setCourses(coursesRes.data);
        setCategories(categoriesRes.data.categories);
        setLevels(levelsRes.data.levels);
        setTotalCount(countRes.data.count);
      } catch (error) {
        console.error("Error fetching courses:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [filters]);

  const updateFilter = (key, value) => {
    const newFilters = { ...filters, [key]: value, page: 1 };
    setFilters(newFilters);
    
    const params = new URLSearchParams();
    Object.entries(newFilters).forEach(([k, v]) => {
      if (v) params.set(k === "certification_level" ? "level" : k === "delivery_format" ? "format" : k, v);
    });
    setSearchParams(params);
  };

  const clearFilters = () => {
    setFilters({
      search: "",
      category: "",
      certification_level: "",
      delivery_format: "",
      page: 1,
    });
    setSearchParams({});
  };

  const hasActiveFilters = filters.category || filters.certification_level || filters.delivery_format || filters.search;

  const totalPages = Math.ceil(totalCount / 12);

  // Generate page title based on filters
  const getPageTitle = () => {
    if (filters.category) return `${filters.category} Training Courses`;
    if (filters.certification_level) return `${filters.certification_level} Certification Courses`;
    return "PECB Certified Training Courses";
  };

  const getPageDescription = () => {
    if (filters.category) {
      return `Browse ${filters.category} certified training courses. Foundation, Lead Implementer, and Lead Auditor levels available. Self-study, online live, and e-learning options.`;
    }
    return "Browse 38+ PECB certified training courses. ISO 9001, ISO 27001, ISO 45001, ISO 14001, ISO 22000, ISO 42001 and more. Flexible learning options with corporate discounts.";
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO
        title={getPageTitle()}
        description={getPageDescription()}
        url="/courses"
        keywords={[
          filters.category || "ISO training",
          "PECB courses",
          "Lead Auditor certification",
          "Lead Implementer training",
          "Foundation course",
          "management system training",
          "Jamaica ISO courses",
          "Caribbean certification"
        ]}
        structuredData={getCourseListSchema(courses)}
      />
      {/* Header */}
      <section className="bg-white border-b border-border py-12">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="font-heading font-bold text-3xl lg:text-4xl text-primary tracking-tight mb-4" data-testid="courses-title">
              PECB Certified Courses
            </h1>
            <p className="text-muted-foreground text-lg">
              Browse our selection of officially certified PECB training courses.
              Select a course below to view details and enroll.
            </p>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4 lg:px-8 py-8">
        {/* Self-Paced Track Toggle */}
        <button
          type="button"
          onClick={() => updateFilter("delivery_format", filters.delivery_format === "e-learning" ? "" : "e-learning")}
          className={`group w-full mb-6 rounded-2xl border-2 p-5 lg:p-6 text-left transition-all ${
            filters.delivery_format === "e-learning"
              ? "border-brand bg-gradient-to-r from-brand/10 via-brand/5 to-transparent shadow-sm"
              : "border-dashed border-brand/30 bg-white hover:border-brand/60 hover:bg-brand/5"
          }`}
          data-testid="self-paced-track-toggle"
        >
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div className={`flex items-center justify-center h-12 w-12 rounded-xl flex-shrink-0 ${
              filters.delivery_format === "e-learning" ? "bg-brand text-white" : "bg-brand/10 text-brand"
            }`}>
              <Zap className="h-6 w-6" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-heading font-semibold text-base lg:text-lg text-primary">
                  Self-Paced E-Learning Track
                </h3>
                {filters.delivery_format === "e-learning" && (
                  <Badge className="bg-brand text-white text-[10px] px-2 py-0.5">Active</Badge>
                )}
              </div>
              <p className="text-sm text-muted-foreground">
                Learn on your own schedule — 19 PECB courses available as online self-paced E-Learning.
                Start instantly, study anywhere, earn the same certification.
              </p>
            </div>
            <div className="flex-shrink-0">
              <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                filters.delivery_format === "e-learning"
                  ? "bg-brand text-white"
                  : "bg-slate-100 text-slate-700 group-hover:bg-brand group-hover:text-white"
              }`}>
                {filters.delivery_format === "e-learning" ? (
                  <>Showing E-Learning <X className="h-4 w-4" /></>
                ) : (
                  <>Browse Self-Paced <ArrowRight className="h-4 w-4" /></>
                )}
              </span>
            </div>
          </div>
        </button>

        <div className="lg:flex lg:gap-8">
          {/* Filter Sidebar - Desktop */}
          <aside className="hidden lg:block w-64 flex-shrink-0">
            <div className="filter-sidebar space-y-6 bg-white p-6 rounded-xl border border-border">
              <div className="flex items-center justify-between">
                <h2 className="font-heading font-semibold text-lg">Filters</h2>
                {hasActiveFilters && (
                  <button
                    onClick={clearFilters}
                    className="text-sm text-brand hover:underline"
                    data-testid="clear-filters-btn"
                  >
                    Clear All
                  </button>
                )}
              </div>

              {/* Search */}
              <div className="space-y-2">
                <Label>Search</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search courses..."
                    value={filters.search}
                    onChange={(e) => updateFilter("search", e.target.value)}
                    className="pl-9"
                    data-testid="search-input"
                  />
                </div>
              </div>

              {/* Category Filter */}
              <div className="space-y-2">
                <Label>Course Category</Label>
                <Select
                  value={filters.category || "all"}
                  onValueChange={(value) => updateFilter("category", value === "all" ? "" : value)}
                >
                  <SelectTrigger data-testid="category-select">
                    <SelectValue placeholder="All Categories" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {categories.map((cat) => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Level Filter */}
              <div className="space-y-2">
                <Label>Certification Level</Label>
                <Select
                  value={filters.certification_level || "all"}
                  onValueChange={(value) => updateFilter("certification_level", value === "all" ? "" : value)}
                >
                  <SelectTrigger data-testid="level-select">
                    <SelectValue placeholder="All Levels" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Levels</SelectItem>
                    {levels.map((level) => (
                      <SelectItem key={level} value={level}>{level}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Delivery Format Filter */}
              <div className="space-y-2">
                <Label>Delivery Format</Label>
                <Select
                  value={filters.delivery_format || "all"}
                  onValueChange={(value) => updateFilter("delivery_format", value === "all" ? "" : value)}
                >
                  <SelectTrigger data-testid="format-select">
                    <SelectValue placeholder="All Formats" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Formats</SelectItem>
                    {deliveryFormats.map((format) => (
                      <SelectItem key={format.value} value={format.value}>{format.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </aside>

          {/* Mobile Filter Button */}
          <div className="lg:hidden mb-4">
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className="w-full"
              data-testid="mobile-filter-btn"
            >
              <Filter className="h-4 w-4 mr-2" />
              Filters
              {hasActiveFilters && (
                <Badge className="ml-2 bg-brand text-white">Active</Badge>
              )}
            </Button>
          </div>

          {/* Mobile Filters */}
          {showFilters && (
            <div className="lg:hidden mb-6 bg-white p-4 rounded-xl border border-border space-y-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search courses..."
                  value={filters.search}
                  onChange={(e) => updateFilter("search", e.target.value)}
                  className="pl-9"
                />
              </div>
              <Select
                value={filters.category || "all"}
                onValueChange={(value) => updateFilter("category", value === "all" ? "" : value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select
                value={filters.certification_level || "all"}
                onValueChange={(value) => updateFilter("certification_level", value === "all" ? "" : value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Levels" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Levels</SelectItem>
                  {levels.map((level) => (
                    <SelectItem key={level} value={level}>{level}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {hasActiveFilters && (
                <Button variant="ghost" onClick={clearFilters} className="w-full">
                  <X className="h-4 w-4 mr-2" /> Clear Filters
                </Button>
              )}
            </div>
          )}

          {/* Course Grid */}
          <div className="flex-1">
            {/* Results count */}
            <div className="flex items-center justify-between mb-6">
              <p className="text-sm text-muted-foreground">
                Showing {courses.length} of {totalCount} courses
              </p>
            </div>

            {loading ? (
              <div className="grid grid-cols-1 gap-6">
                {[1, 2, 3, 4].map((i) => (
                  <Card key={i} className="overflow-hidden">
                    <div className="flex">
                      <div className="w-48 h-48 skeleton flex-shrink-0" />
                      <CardContent className="p-6 flex-1">
                        <div className="h-4 w-1/4 skeleton rounded mb-2" />
                        <div className="h-6 w-3/4 skeleton rounded mb-2" />
                        <div className="h-4 w-full skeleton rounded mb-4" />
                        <div className="h-10 w-1/3 skeleton rounded" />
                      </CardContent>
                    </div>
                  </Card>
                ))}
              </div>
            ) : courses.length === 0 ? (
              <div className="text-center py-16">
                <BookOpen className="h-16 w-16 mx-auto text-muted-foreground/50 mb-4" />
                <h3 className="font-heading font-semibold text-xl mb-2">No courses found</h3>
                <p className="text-muted-foreground mb-4">Try adjusting your filters or search terms</p>
                <Button variant="outline" onClick={clearFilters}>Clear Filters</Button>
              </div>
            ) : (
              <div className="space-y-6">
                {courses.map((course) => (
                  <Card key={course.id} className="overflow-hidden course-card border-border/50 hover:border-brand/50" data-testid={`course-card-${course.id}`}>
                    <div className="md:flex">
                      <div 
                        className="w-full md:w-48 h-48 md:h-auto bg-cover bg-center flex-shrink-0"
                        style={{ backgroundImage: `url(${course.image_url || 'https://images.pexels.com/photos/3183197/pexels-photo-3183197.jpeg'})` }}
                      />
                      <CardContent className="p-6 flex-1">
                        <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
                          <div>
                            {PROMO_ACTIVE && (
                              <Badge className="bg-red-500 text-white text-[10px] px-1.5 py-0.5 mb-1 mr-2">
                                {PROMO_BADGE_TEXT}
                              </Badge>
                            )}
                            <Badge variant="outline" className="text-accent border-accent/30 mb-2">
                              {course.certification_level}
                            </Badge>
                            <SelfPacedBadge course={course} className="mb-2 ml-2 align-middle" />
                            <h3 className="font-heading font-semibold text-xl text-primary">
                              {course.title}
                            </h3>
                          </div>
                          <PromoPriceDisplay 
                            price={course.delivery_formats.find(f => f.format === fmtOf(course))?.price || Math.min(...course.delivery_formats.map(f => f.price))}
                            size="default"
                            showBadge={false}
                          />
                        </div>
                        
                        <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                          {course.description}
                        </p>

                        {/* Delivery Format Selection */}
                        <div className="mb-4">
                          <span className="text-sm font-medium block mb-2 sm:inline sm:mb-0 sm:mr-3">Format:</span>
                          <RadioGroup
                            value={fmtOf(course)}
                            onValueChange={(value) => handleFormatChange(course.id, value)}
                            className="flex flex-wrap gap-2"
                          >
                            {course.delivery_formats.map((format) => (
                              <div key={format.format} className="flex items-center">
                                <RadioGroupItem
                                  value={format.format}
                                  id={`${course.id}-${format.format}`}
                                  className="peer sr-only"
                                  data-testid={`format-${course.id}-${format.format}`}
                                />
                                <Label
                                  htmlFor={`${course.id}-${format.format}`}
                                  className="px-3 py-1.5 text-xs sm:text-sm border rounded-full cursor-pointer peer-data-[state=checked]:border-brand peer-data-[state=checked]:bg-brand/10 peer-data-[state=checked]:text-brand peer-data-[state=checked]:font-medium hover:border-brand/50 transition-all whitespace-nowrap"
                                >
                                  {format.format === "self-study" && "Self-Study"}
                                  {format.format === "online-live" && "Online Live"}
                                  {format.format === "e-learning" && "E-Learning"}
                                  <PromoInlinePrice price={format.price} />
                                </Label>
                              </div>
                            ))}
                          </RadioGroup>
                        </div>

                        {/* Planned cohort dates (scheduled live online classes) */}
                        {upcomingCohorts(course, "online-live").length > 0 && (
                          <div className="mb-4 p-3 bg-brand/5 border border-brand/20 rounded-lg" data-testid={`cohort-dates-${course.id}`}>
                            <div className="flex items-center gap-2 mb-2">
                              <Calendar className="h-4 w-4 text-brand" />
                              <span className="text-sm font-medium text-charcoal">Upcoming Live Online Dates</span>
                              {upcomingCohorts(course, "online-live")[0].timezone && <span className="text-xs text-muted-foreground">({upcomingCohorts(course, "online-live")[0].timezone})</span>}
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {upcomingCohorts(course, "online-live").map((c) => (
                                <Link
                                  key={c.id}
                                  to={`/courses/${course.id}?format=online-live&cohort=${c.id}`}
                                  className="px-3 py-1.5 text-xs sm:text-sm bg-white border rounded-full hover:border-brand hover:text-brand transition-colors"
                                  data-testid={`card-cohort-${course.id}-${c.id}`}
                                >
                                  <span className="font-semibold">{cohortMonth(c).slice(0, 3)} {cohortDayRange(c)}</span>, {cohortYear(c)}
                                </Link>
                              ))}
                            </div>
                            {upcomingCohorts(course, "online-live")[0].trainer_name && (
                              <p className="text-xs text-muted-foreground mt-2">Trainer: {upcomingCohorts(course, "online-live")[0].trainer_name}</p>
                            )}
                          </div>
                        )}

                        {/* Show schedules for Online Live */}
                        {fmtOf(course) === "online-live" && upcomingCohorts(course, "online-live").length === 0 && (
                          <div className="mb-4 p-3 bg-blue-50 border border-blue-100 rounded-lg" data-testid={`schedules-${course.id}`}>
                            <div className="flex items-center gap-2 mb-2">
                              <Calendar className="h-4 w-4 text-blue-600" />
                              <span className="text-sm font-medium text-blue-900">Available Sessions</span>
                            </div>
                            {loadingSchedules[course.id] ? (
                              <div className="text-sm text-blue-600">Loading schedules...</div>
                            ) : courseSchedules[course.id]?.length > 0 ? (
                              <div className="space-y-2">
                                {courseSchedules[course.id].slice(0, 3).map((schedule) => (
                                  <div key={schedule.id} className="flex flex-wrap items-center gap-3 text-sm">
                                    <span className="font-medium text-blue-900">
                                      {formatScheduleDate(schedule.start_date)} - {formatScheduleDate(schedule.end_date)}
                                    </span>
                                    <span className="text-blue-700 flex items-center gap-1">
                                      <Clock className="h-3 w-3" />
                                      {schedule.start_time} - {schedule.end_time}
                                    </span>
                                    <span className="text-blue-600 flex items-center gap-1">
                                      <Users className="h-3 w-3" />
                                      {schedule.max_participants - schedule.enrolled_count} spots left
                                    </span>
                                  </div>
                                ))}
                                {courseSchedules[course.id].length > 3 && (
                                  <p className="text-xs text-blue-600">+{courseSchedules[course.id].length - 3} more sessions available</p>
                                )}
                              </div>
                            ) : (
                              <p className="text-sm text-blue-600">No scheduled sessions yet. Check back soon or contact us for custom scheduling.</p>
                            )}
                          </div>
                        )}

                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                          <Link to={`/courses/${course.id}?format=${fmtOf(course)}`} className="w-full sm:w-auto">
                            <Button className="w-full sm:w-auto bg-brand hover:bg-brand-dark text-white" data-testid={`view-details-${course.id}`}>
                              View Details & Enroll
                              <ArrowRight className="ml-2 h-4 w-4" />
                            </Button>
                          </Link>
                          {course.brochure_url && (
                            <a
                              href={course.brochure_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-brand border border-brand/30 rounded-lg hover:bg-brand/5 transition-colors w-full sm:w-auto"
                              data-testid={`brochure-${course.id}`}
                            >
                              <BookOpen className="h-4 w-4" />
                              <span className="sm:inline">Brochure</span>
                            </a>
                          )}
                        </div>
                      </CardContent>
                    </div>
                  </Card>
                ))}
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-8">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={filters.page === 1}
                  onClick={() => updateFilter("page", filters.page - 1)}
                >
                  Previous
                </Button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => i + 1).map((page) => (
                  <Button
                    key={page}
                    variant={filters.page === page ? "default" : "outline"}
                    size="sm"
                    onClick={() => updateFilter("page", page)}
                    className={filters.page === page ? "bg-brand text-white" : ""}
                  >
                    {page}
                  </Button>
                ))}
                {totalPages > 5 && <span className="px-2">...</span>}
                <Button
                  variant="outline"
                  size="sm"
                  disabled={filters.page === totalPages}
                  onClick={() => updateFilter("page", filters.page + 1)}
                >
                  Next
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Courses;
